// FooterSection.js — Footer personalizable para landing
import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { BACKEND_BASE } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { buildBg } from '../bgHelpers';
import CustomHtmlSection from './CustomHtmlSection';
import FooterStreaming from './FooterStreaming';
import { PieCurva } from './PortadaCursoSecciones';

/* Variantes:
   - $flotante=false: footer estático normal (toma el ancho)
   - $flotante=true:  footer "flotante" position fixed, anclado al lado configurado
*/
const Wrapper = styled.footer`
  position: relative;
  /* Con width:100% el padding sumaba al ancho y sacaba scroll horizontal. */
  box-sizing: border-box;
  background: ${p => p.$bg || '#0f355b'};
  color: ${p => p.$color || '#fff'};
  padding: ${p => p.$padding || '3rem 2rem 1.5rem'};
  /* Esquinas redondeadas del footer estático (p. ej. "0 200px 0 0" para recortar
     la esquina superior derecha). Solo aplica cuando NO es flotante: el flotante
     ya tiene su propio radio. */
  ${p => !p.$flotante && p.$radioEsquinas ? `border-radius: ${p.$radioEsquinas};` : ''}
  ${p => !p.$flotante && p.$radioEsquinas ? '@media (max-width: 768px) { border-radius: 0; }' : ''}
  ${p => !p.$flotante && css`width: 100%;`}
  ${p => p.$flotante && css`
    position: ${p.$tipoFlotante || 'fixed'};
    z-index: 950;
    box-shadow: ${p.$sinSombra ? 'none' : '0 8px 32px rgba(0,0,0,0.25)'};
    border-radius: ${p.$radio || '14px'};
    /* Ancho exacto — si el admin pidió 1200px, el footer mide 1200px.
       Precedencia: posicion_flotante_ancho_max > ancho_container > 420px. */
    width: ${p.$maxWidth || p.$containerMax || '420px'};
    max-width: calc(100vw - 24px);
    ${p.$lado === 'bottom-left' && `bottom: ${p.$margen || '20px'}; left: ${p.$margen || '20px'};`}
    ${p.$lado === 'bottom-right' && `bottom: ${p.$margen || '20px'}; right: ${p.$margen || '20px'};`}
    ${p.$lado === 'bottom-center' && `bottom: ${p.$margen || '20px'}; left: 50%; transform: translateX(-50%);`}
    ${p.$lado === 'right' && `top: 50%; right: ${p.$margen || '20px'}; transform: translateY(-50%);`}
    ${p.$lado === 'left' && `top: 50%; left: ${p.$margen || '20px'}; transform: translateY(-50%);`}
    ${p.$lado === 'top-right' && `top: ${p.$margen || '20px'}; right: ${p.$margen || '20px'};`}
    ${p.$lado === 'top-left' && `top: ${p.$margen || '20px'}; left: ${p.$margen || '20px'};`}
    /* En móvil, footer flotante se monta encima del contenido y tapa texto.
       Volvemos al flujo normal: position relative, ancho 100%, sin offsets. */
    @media (max-width: 768px) {
      position: relative;
      top: auto;
      bottom: auto;
      left: auto;
      right: auto;
      transform: none;
      width: 100%;
      max-width: 100%;
      border-radius: 0;
      box-shadow: none;
    }
  `}
`;

/* Franja degradada pegada al borde superior del footer (el remate del diseño
   cuando el footer arranca directo, sin banda de aval encima). */
const LineaSuperior = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0;
  height: ${p => p.$alto || '10px'};
  background: ${p => p.$bg};
  z-index: 2;
`;

/* Adornos anclados a los costados (las "olas" del diseño). Son decorativos:
   no reciben clics y se ocultan en móvil si el admin lo pide. */
const FondoLateral = styled.img`
  position: absolute;
  bottom: 0;
  ${p => p.$lado === 'derecha' ? 'right: 0;' : 'left: 0;'}
  width: ${p => p.$ancho || '200px'};
  height: auto;
  pointer-events: none;
  user-select: none;
  z-index: 0;
  @media (max-width: 600px) {
    ${p => p.$ocultarMovil ? 'display: none;' : `width: ${p.$anchoMovil || '110px'};`}
  }
`;

/* Banda de aval: la franja que va justo encima del footer (el sello de la
   sociedad que avala el proyecto). Al vivir dentro del footer aparece igual en
   todas las páginas, y sus variantes por país la cambian u ocultan. */
const AvalBanda = styled.div`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  background: ${p => p.$bg || '#012965'};
  min-height: ${p => p.$alto || '190px'};
  padding: ${p => p.$padding || '1rem 1.5rem'};
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: 600px) {
    min-height: ${p => p.$altoMovil || p.$alto || '170px'};
    padding: ${p => p.$paddingMovil || p.$padding || '1rem'};
  }
`;

const AvalLogo = styled.img`
  width: ${p => p.$ancho || '226px'};
  max-width: 100%;
  height: auto;
  display: block;
  @media (max-width: 600px) { width: ${p => p.$anchoMovil || p.$ancho || '180px'}; }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: ${p => p.$container === false ? '100%' : (p.$maxWidth || '1200px')};
  margin: 0 auto;
  padding: ${p => p.$padding || '0'};
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: ${p => p.$alignItems || 'flex-start'};
  flex-wrap: wrap;
  gap: 2rem;
  margin-bottom: ${p => p.$noMargin ? '0' : '2rem'};
  flex-direction: ${p => p.$inversa ? 'row-reverse' : 'row'};
  @media (max-width: 768px) { flex-direction: column; align-items: center; text-align: center; }
`;

/* Helper para mapear alineación a estilo */
const alignStyles = (a) => {
  if (a === 'right')  return 'text-align: right;  align-items: flex-end;';
  if (a === 'center') return 'text-align: center; align-items: center;';
  return                     'text-align: left;   align-items: flex-start;';
};

const BrandCol = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  ${p => alignStyles(p.$alignDesktop || (p.$alignRight ? 'right' : 'left'))}
  @media (max-width: 991px) and (min-width: 601px) {
    ${p => alignStyles(p.$alignTablet || p.$alignDesktop || (p.$alignRight ? 'right' : 'left'))}
  }
  @media (max-width: 600px) {
    ${p => alignStyles(p.$alignMobile || p.$alignTablet || p.$alignDesktop || 'center')}
  }
`;

const Logo = styled.img`
  height: ${p => p.$h || '40px'};
  width: ${p => p.$w || 'auto'};
  max-width: 100%;
  object-fit: contain;
  margin-bottom: 0.75rem;
  /* Vuelve el logo blanco sólido, sin necesitar una versión blanca del archivo. */
  ${p => p.$blanco && 'filter: brightness(0) invert(1);'}
`;

const BrandName = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const BrandDesc = styled.p`
  font-size: 0.88rem;
  opacity: 0.8;
  line-height: 1.5;
  margin: 0;
`;

const LinksCol = styled.div`
  display: flex;
  gap: 3rem;
  flex-wrap: wrap;
  @media (max-width: 768px) { gap: 2rem; justify-content: center; }
`;

const LinkGroup = styled.div``;

const LinkGroupTitle = styled.h4`
  font-size: 0.9rem;
  font-weight: 700;
  margin: 0 0 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.7;
`;

const LinkItem = styled.a`
  display: block;
  color: inherit;
  text-decoration: none;
  font-size: 0.88rem;
  padding: 0.2rem 0;
  opacity: 0.85;
  transition: 0.2s;
  cursor: pointer;
  &:hover { opacity: 1; text-decoration: underline; }
`;

/* Divisor sobre el copyright. Un color plano se pinta como borde (lo de
   siempre); un degradado necesita ser fondo, y ahí manda `divisor_alto`. */
const Divider = styled.hr`
  border: none;
  ${p => p.$degradado
    ? `height: ${p.$alto || '3px'}; background: ${p.$color};`
    : `border-top: ${p.$alto || '1px'} solid ${p.$color || 'rgba(255,255,255,0.15)'};`}
  margin: ${p => p.$margin || '0 0 1rem'};
`;

/* Caja de aviso legal / disclaimer: texto largo y chico dentro de un recuadro
   con borde. Acepta HTML (párrafos, <strong>, enlaces). */
const Disclaimer = styled.div`
  max-width: ${p => p.$maxWidth || '100%'};
  margin: ${p => p.$margin || '0 auto'};
  border: ${p => p.$border || '1px solid rgba(255,255,255,0.6)'};
  border-radius: ${p => p.$radius || '10px'};
  padding: ${p => p.$padding || '10px'};
  color: ${p => p.$color || 'inherit'};
  font-size: ${p => p.$size || '9px'};
  line-height: ${p => p.$lineHeight || '1.3'};
  text-align: ${p => p.$align || 'justify'};
  p { margin: 0 0 0.4em; }
  p:last-child { margin-bottom: 0; }
  a {
    color: ${p => p.$linkColor || '#a3e4ff'};
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
  @media (max-width: 600px) {
    text-align: ${p => p.$alignMobile || 'left'};
    font-size: ${p => p.$sizeMobile || p.$size || '9px'};
  }
`;

/* Fila de enlaces al pie del footer (p. ej. «Aviso legal y condiciones»). */
const BottomLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: ${p => justifyFor(p.$align || 'center')};
  gap: ${p => p.$gap || '1.5rem'};
  margin: ${p => p.$margin || '1.5rem 0 0'};
  @media (max-width: 600px) { justify-content: center; }
`;

const BottomSep = styled.span`
  color: ${p => p.$color || 'inherit'};
  font-size: ${p => p.$size || '1rem'};
  opacity: 0.6;
  user-select: none;
`;
const BottomLink = styled.a`
  color: ${p => p.$color || 'inherit'};
  font-size: ${p => p.$size || '1rem'};
  font-weight: ${p => p.$weight || '400'};
  text-transform: ${p => p.$transform || 'none'};
  text-decoration: ${p => p.$subrayado === false ? 'none' : 'underline'};
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.8; }
`;

/* Para BottomRow: aplica per-device justify-content + text-align según la
   alineación pedida (left | center | right). */
const justifyFor = (a) => (a === 'right' ? 'flex-end' : a === 'center' ? 'center' : 'flex-start');

const BottomRow = styled.div`
  display: flex;
  justify-content: ${p => p.$dividido ? 'space-between' : justifyFor(p.$alignDesktop)};
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: ${p => p.$size || '0.82rem'};
  /* Con color propio se pinta tal cual; sin él, el gris translúcido de siempre
     sobre el color del footer. */
  ${p => p.$color ? `color: ${p.$color};` : 'opacity: 0.7;'}
  text-align: ${p => p.$alignDesktop || 'inherit'};
  @media (max-width: 991px) and (min-width: 601px) {
    justify-content: ${p => p.$dividido ? 'space-between' : justifyFor(p.$alignTablet || p.$alignDesktop)};
    text-align: ${p => (p.$alignTablet || p.$alignDesktop) || 'inherit'};
  }
  @media (max-width: 600px) {
    justify-content: ${p => p.$dividido ? 'center' : justifyFor(p.$alignMobile || p.$alignTablet || p.$alignDesktop || 'center')};
    text-align: ${p => p.$alignMobile || p.$alignTablet || p.$alignDesktop || 'center'};
    flex-direction: column;
  }
`;

/* Cada mitad del copyright (izquierda = texto, derecha = imagen/texto) */
const BottomSide = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  @media (max-width: 600px) { justify-content: center; }
`;
/* Si se fija el ancho y no la altura, la altura va libre: con el alto por
   defecto (32px) y `contain`, una imagen más alta se encogía dentro de la caja
   y nunca llegaba a medir el ancho pedido. */
const CopyImg = styled.img`
  height: ${p => p.$h || (p.$w ? 'auto' : '32px')};
  width: ${p => p.$w || 'auto'};
  max-width: 100%;
  object-fit: contain;
`;

/* Modo barra: sin columnas de enlaces. A la izquierda el copyright sobre el
   logo, a la derecha el bloque "Avalado por: [sello]" centrado vertical. */
const BarCopyright = styled.div`
  font-size: ${p => p.$size || '0.82rem'};
  color: ${p => p.$color || 'inherit'};
  line-height: 1.5;
  margin-bottom: 0.9rem;
`;

const RightBlock = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.$gap || '0.75rem'};
  flex-wrap: wrap;
  font-size: ${p => p.$size || '0.88rem'};
  color: ${p => p.$color || 'inherit'};
  @media (max-width: 768px) { justify-content: center; }
`;

/* El bloque derecho puede llevar un segundo sello debajo del primero (p. ej.
   los logos arriba y el aval de una sociedad científica abajo). Sin la segunda
   imagen se comporta exactamente como antes. */
const RightStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${p => p.$align || 'flex-end'};
  gap: ${p => p.$gap || '0.9rem'};
  @media (max-width: 768px) { align-items: center; }
`;

/* ── Tira de logos (arriba del footer) ──────────────────────────────────
   Dos modos: carrusel automático (marquee) o grilla estática. En marquee la
   lista se duplica y el track recorre translateX(-50%), que equivale justo a
   un set → el bucle es continuo y sin salto. */
const marqueeAnim = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

const LogoStrip = styled.div`
  /* El margen puede venir como un solo valor inferior ("2rem") o como shorthand
     completo ("0 auto 2rem"). Un shorthand sin "auto" deja de centrar la tira,
     por eso solo se respeta tal cual cuando el admin escribió varios valores. */
  margin: ${p => (p.$mb && /\s/.test(p.$mb.trim())) ? p.$mb : `0 auto ${p.$mb || '2.25rem'}`};
  align-items: center;
  ${p => p.$divisor ? `border-bottom: 1px solid ${p.$divisor};` : ''}
  ${p => p.$marquee ? css`
    display: block;
    overflow: hidden;
    max-width: 100%;
    padding-bottom: 1.75rem;
    /* Máscara: los logos entran y salen difuminados por los bordes. */
    -webkit-mask-image: linear-gradient(to right, transparent, #000 6%, #000 94%, transparent);
    mask-image: linear-gradient(to right, transparent, #000 6%, #000 94%, transparent);
  ` : css`
    max-width: 1200px;
    padding: ${p.$padding || '0 2rem 1.75rem'};
    display: grid;
    grid-template-columns: repeat(${p => p.$perDesktop || 5}, 1fr);
    gap: ${p => p.$gap || '32px'};
    justify-items: center;
    @media (max-width: 991px) and (min-width: 601px) {
      grid-template-columns: repeat(${p => p.$perTablet || 3}, 1fr);
    }
    @media (max-width: 600px) {
      grid-template-columns: repeat(${p => p.$perMobile || 2}, 1fr);
    }
  `}
`;

const StripTrack = styled.div`
  ${p => p.$marquee ? css`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    width: max-content;
    gap: ${p.$gap || '32px'};
    animation: ${marqueeAnim} ${p.$dur || '30s'} linear infinite;
    will-change: transform;
    &:hover { animation-play-state: paused; }
    /* Respeta a quien pidió menos movimiento en su sistema. */
    @media (prefers-reduced-motion: reduce) { animation: none; }
  ` : css`display: contents;`}
`;

const StripItem = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  ${p => p.$marquee && 'flex: 0 0 auto;'}
  img {
    max-width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
    max-height: ${p => p.$h || '48px'};
    /* Vuelve blanco cualquier logo, sin importar su color original. */
    ${p => p.$blanco && 'filter: brightness(0) invert(1);'}
  }
`;

const FooterLogoStrip = ({ c, resolveUrl }) => {
  const logos = (Array.isArray(c.logos_strip) ? c.logos_strip : []).filter(l => l && l.url);
  const marquee = c.logos_strip_marquee !== false;
  const trackRef = useRef(null);
  const [dur, setDur] = useState('30s');

  // Duración = ancho de un set / velocidad (px por segundo). Así la velocidad
  // se ve igual con 3 logos que con 20.
  const recalc = useCallback(() => {
    if (!marquee || !trackRef.current) return;
    const oneSet = trackRef.current.scrollWidth / 2;
    const speed = Number(c.logos_strip_velocidad) || 40;
    const d = oneSet > 0 && speed > 0 ? oneSet / speed : 30;
    setDur(d.toFixed(2) + 's');
  }, [marquee, c.logos_strip_velocidad]);

  useEffect(() => {
    recalc();
    if (!marquee || !trackRef.current || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(recalc);
    ro.observe(trackRef.current);
    window.addEventListener('resize', recalc);
    return () => { ro.disconnect(); window.removeEventListener('resize', recalc); };
  }, [recalc, marquee, logos.length]);

  if (!logos.length) return null;

  const gap = (c.logos_strip_gap || 32) + 'px';
  const altura = (c.logos_strip_altura || 48) + 'px';
  // En marquee se duplica la lista: la 2ª copia es decorativa (aria-hidden).
  const items = marquee ? [...logos, ...logos] : logos;

  return (
    <LogoStrip
      $marquee={marquee}
      $gap={gap}
      $mb={c.logos_strip_margen}
      $padding={c.logos_strip_padding}
      $divisor={c.logos_strip_divisor}
      $perDesktop={c.logos_strip_por_fila_desktop}
      $perTablet={c.logos_strip_por_fila_tablet}
      $perMobile={c.logos_strip_por_fila_mobile}
    >
      <StripTrack ref={trackRef} $marquee={marquee} $gap={gap} $dur={dur}>
        {items.map((l, i) => {
          const dup = marquee && i >= logos.length;
          const href = l.link || undefined;
          return (
            <StripItem
              key={i}
              as={href ? 'a' : 'span'}
              href={href}
              target={href && l.nueva_pestana ? '_blank' : undefined}
              rel={href && l.nueva_pestana ? 'noopener noreferrer' : undefined}
              aria-hidden={dup ? 'true' : undefined}
              tabIndex={dup ? -1 : undefined}
              $marquee={marquee}
              /* Alto propio del logo (px) cuando el diseño pide tamaños
                 distintos por marca; si no, el alto común de la tira. */
              $h={l.alto ? `${l.alto}px` : altura}
              $blanco={c.logos_strip_blanco === true}
            >
              <img src={resolveUrl(l.url)} alt={l.alt || ''} />
            </StripItem>
          );
        })}
      </StripTrack>
    </LogoStrip>
  );
};

const FooterSection = ({ config, proyecto, openLogin, openRegister, tl, sinRadio }) => {
  const c = config;
  const { user } = useAuth();
  const primary = c.color_primario || proyecto?.color_primario || '#0f355b';

  // El recorte de esquinas puede ser distinto para quien no ha entrado (las
  // páginas públicas tienen otra composición). Sin la clave se usa el de
  // siempre; un "0" deja el footer recto.
  // `sinRadio` lo pone el layout cuando la página actual está en la lista de
  // rutas sin recorte (config.rutas_sin_radio).
  const radioEsquinas = sinRadio
    ? null
    : ((!user && c.radio_esquinas_sin_sesion !== undefined && c.radio_esquinas_sin_sesion !== '')
      ? c.radio_esquinas_sin_sesion
      : c.radio_esquinas);

  const resolveUrl = (u) => (u && u.startsWith('http') ? u : `${BACKEND_BASE}/${u}`);

  const logoUrl = c.logo
    ? (c.logo.startsWith('http') ? c.logo : `${BACKEND_BASE}/${c.logo}`)
    : (proyecto?.logo ? `${BACKEND_BASE}/${proyecto.logo}` : null);

  // Imagen opcional del copyright (lado derecho cuando el copyright está "dividido")
  // Segundo sello del bloque derecho (aval), debajo de los logos.
  const avalImg = c.aval_derecha
    ? (c.aval_derecha.startsWith('http') ? c.aval_derecha : `${BACKEND_BASE}/${c.aval_derecha.replace(/^\//, '')}`)
    : '';
  const copyrightImg = c.copyright_imagen
    ? (c.copyright_imagen.startsWith('http') ? c.copyright_imagen : `${BACKEND_BASE}/${c.copyright_imagen}`)
    : null;

  const columnas = c.columnas || [];

  const handleLink = (link) => {
    if (link.accion === 'login') openLogin?.();
    else if (link.accion === 'registro') openRegister?.();
    else if (link.url) window.open(link.url, '_blank');
  };

  // La banda de aval se puede ocultar por país desde las variantes de audiencia
  // (aval_oculto), igual que la sección de la landing que reemplaza.
  const avalVisible = c.aval_activo === true && !!c.aval_logo && c.aval_oculto !== true;

  return (
    <>
    {avalVisible && (
      <AvalBanda
        $bg={c.aval_color_fondo}
        $alto={c.aval_alto}
        $altoMovil={c.aval_alto_movil}
        $padding={c.aval_padding}
        $paddingMovil={c.aval_padding_movil}
      >
        {c.aval_linea_superior && (
          <LineaSuperior $bg={c.aval_linea_superior} $alto={c.aval_linea_superior_alto} />
        )}
        <AvalLogo
          src={resolveUrl(c.aval_logo)}
          alt={tl(c.aval_logo_alt) || ''}
          $ancho={c.aval_logo_ancho}
          $anchoMovil={c.aval_logo_ancho_movil}
        />
      </AvalBanda>
    )}
    <Wrapper
      $bg={buildBg(c, primary)}
      $color={c.color_texto || '#fff'}
      $padding={c.padding}
      $flotante={c.posicion_flotante === true}
      $lado={c.posicion_flotante_lado || 'bottom-right'}
      $margen={c.posicion_flotante_margen}
      $maxWidth={c.posicion_flotante_ancho_max}
      $containerMax={c.usar_container !== false ? c.ancho_container : null}
      $radio={c.posicion_flotante_radio}
      $radioEsquinas={radioEsquinas}
      $sinSombra={c.flotante_sin_sombra === true}
      $tipoFlotante={c.posicion_flotante_tipo || 'fixed'}
    >
      {c.linea_superior && <LineaSuperior $bg={c.linea_superior} $alto={c.linea_superior_alto} />}
      {c.fondo_lateral_izq && (
        <FondoLateral
          src={resolveUrl(c.fondo_lateral_izq)}
          alt=""
          aria-hidden="true"
          $lado="izquierda"
          $ancho={c.fondo_lateral_ancho}
          $anchoMovil={c.fondo_lateral_ancho_movil}
          $ocultarMovil={c.fondo_lateral_ocultar_movil === true}
        />
      )}
      {c.fondo_lateral_der && (
        <FondoLateral
          src={resolveUrl(c.fondo_lateral_der)}
          alt=""
          aria-hidden="true"
          $lado="derecha"
          $ancho={c.fondo_lateral_ancho}
          $anchoMovil={c.fondo_lateral_ancho_movil}
          $ocultarMovil={c.fondo_lateral_ocultar_movil === true}
        />
      )}
      {c.logos_strip_activo === true && <FooterLogoStrip c={c} resolveUrl={resolveUrl} />}
      {(() => {
        const showLogo = c.mostrar_logo !== false && logoUrl;
        const showName = c.mostrar_nombre_marca !== false && c.nombre_marca;
        const showDesc = c.mostrar_descripcion !== false && c.descripcion;
        // Modo barra: una sola fila (copyright + logo | bloque derecho), sin
        // columnas de enlaces, divisor ni fila inferior aparte.
        const modoBarra = c.modo_barra === true;
        const showBrandCol = showLogo || showName || showDesc || modoBarra;
        // El copyright admite HTML (<strong>, <br>, enlaces): los avisos legales
        // de farma necesitan resaltar el nombre del producto y separar el codigo
        // de aprobacion en otra linea. Los textos planos de siempre se pintan igual.
        const copyTexto = tl(c.texto_copyright) || `© ${new Date().getFullYear()} ${proyecto?.nombre || 'MentorIA'}. Todos los derechos reservados.`;
        return (
      <Inner $container={c.usar_container !== false} $maxWidth={c.ancho_container} $padding={c.padding_container}>
        {(showBrandCol || columnas.length > 0 || (modoBarra && (copyrightImg || c.texto_derecha))) && (
        <TopRow $inversa={c.disposicion_inversa === true} $alignItems={modoBarra ? 'center' : c.disposicion_align} $noMargin={modoBarra}>
          {showBrandCol && (() => {
            // Alineación per-device del bloque de marca (logo + nombre + descripción)
            const al = c.alineacion_logo;
            const alD = al && typeof al === 'object' ? al.desktop : (typeof al === 'string' ? al : null);
            const alT = al && typeof al === 'object' ? al.tablet  : null;
            const alM = al && typeof al === 'object' ? al.mobile  : null;
            return (
              <BrandCol
                $alignRight={c.disposicion_inversa === true && !alD}
                $alignDesktop={alD}
                $alignTablet={alT}
                $alignMobile={alM}
              >
                {modoBarra && (
                  <BarCopyright
                    $size={c.tamano_copyright}
                    $color={c.color_copyright}
                    dangerouslySetInnerHTML={{ __html: copyTexto }}
                  />
                )}
                {showLogo && <Logo src={logoUrl} $h={c.altura_logo} $w={c.ancho_logo} $blanco={c.logo_blanco === true} />}
                {showName && <BrandName>{tl(c.nombre_marca)}</BrandName>}
                {showDesc && <BrandDesc>{tl(c.descripcion)}</BrandDesc>}
              </BrandCol>
            );
          })()}
          {modoBarra && (copyrightImg || c.texto_derecha || avalImg) && (
            <RightStack $gap={c.bloque_derecho_separacion} $align={c.bloque_derecho_alineacion}>
              {(copyrightImg || c.texto_derecha) && (
                <RightBlock $gap={c.bloque_derecho_gap} $size={c.tamano_texto_derecha} $color={c.color_texto_derecha}>
                  {c.texto_derecha && <span>{tl(c.texto_derecha)}</span>}
                  {copyrightImg && <CopyImg src={copyrightImg} $h={c.copyright_imagen_altura} $w={c.copyright_imagen_ancho} />}
                </RightBlock>
              )}
              {avalImg && (
                <CopyImg src={avalImg} alt={tl(c.aval_derecha_alt, '')} $h={c.aval_derecha_altura} $w={c.aval_derecha_ancho} />
              )}
            </RightStack>
          )}
          {columnas.length > 0 && (
            <LinksCol>
              {columnas.map((col, i) => (
                <LinkGroup key={i}>
                  {col.titulo && col.ocultar_titulo !== true && <LinkGroupTitle>{tl(col.titulo)}</LinkGroupTitle>}
                  {(col.links || []).map((link, j) => (
                    <LinkItem key={j} onClick={() => handleLink(link)}>
                      {tl(link.texto)}
                    </LinkItem>
                  ))}
                </LinkGroup>
              ))}
            </LinksCol>
          )}
        </TopRow>
        )}
        {c.disclaimer_activo === true && tl(c.disclaimer_html) && (
          <Disclaimer
            $maxWidth={c.disclaimer_ancho_maximo}
            $margin={c.disclaimer_margen}
            $border={c.disclaimer_borde}
            $radius={c.disclaimer_radio}
            $padding={c.disclaimer_padding}
            $color={c.disclaimer_color}
            $size={c.disclaimer_tamano}
            $sizeMobile={c.disclaimer_tamano_movil}
            $lineHeight={c.disclaimer_interlineado}
            $align={c.disclaimer_alineacion}
            $alignMobile={c.disclaimer_alineacion_movil}
            $linkColor={c.disclaimer_color_enlace}
            dangerouslySetInnerHTML={{ __html: tl(c.disclaimer_html) }}
          />
        )}
        {(() => {
          const enlaces = (Array.isArray(c.enlaces_inferiores) ? c.enlaces_inferiores : []).filter(l => l && l.texto);
          if (!enlaces.length) return null;
          return (
            <BottomLinks
              $align={c.enlaces_inferiores_alineacion}
              $gap={c.enlaces_inferiores_gap}
              $margin={c.enlaces_inferiores_margen}
            >
              {enlaces.map((l, i) => {
                const externo = l.url && /^(https?:\/\/|mailto:|tel:)/i.test(l.url);
                return (
                  <React.Fragment key={i}>
                  {/* Separador entre enlaces («Políticas | Términos»). Sin la
                      clave los enlaces quedan solo separados por el hueco. */}
                  {i > 0 && c.enlaces_inferiores_separador && (
                    <BottomSep $color={c.enlaces_inferiores_color} $size={c.enlaces_inferiores_tamano}>
                      {c.enlaces_inferiores_separador}
                    </BottomSep>
                  )}
                  <BottomLink
                    href={l.url || undefined}
                    target={externo && l.nueva_pestana ? '_blank' : undefined}
                    rel={externo && l.nueva_pestana ? 'noopener noreferrer' : undefined}
                    $color={c.enlaces_inferiores_color}
                    $size={c.enlaces_inferiores_tamano}
                    $weight={c.enlaces_inferiores_peso}
                    $transform={c.enlaces_inferiores_transform}
                    $subrayado={c.enlaces_inferiores_subrayado}
                  >
                    {tl(l.texto)}
                  </BottomLink>
                  </React.Fragment>
                );
              })}
            </BottomLinks>
          );
        })()}
        {!modoBarra && c.mostrar_copyright !== false && c.mostrar_divisor !== false && <Divider
            $color={c.color_divisor}
            $alto={c.divisor_alto}
            $degradado={typeof c.color_divisor === 'string' && c.color_divisor.includes('gradient')}
            $margin={c.margen_divisor}
          />}
        {!modoBarra && c.mostrar_copyright !== false && (() => {
          const al = c.alineacion_copyright;
          // Soporta string (legacy) o objeto {desktop,tablet,mobile}
          const alD = al && typeof al === 'object' ? al.desktop : (typeof al === 'string' ? (al || null) : null);
          const alT = al && typeof al === 'object' ? al.tablet  : null;
          const alM = al && typeof al === 'object' ? al.mobile  : null;
          return (
        <BottomRow
          $alignDesktop={alD}
          $alignTablet={alT}
          $alignMobile={alM}
          $size={c.tamano_copyright}
          $color={c.color_copyright}
          $dividido={c.copyright_dividido === true}
        >
          <BottomSide>
            <span dangerouslySetInnerHTML={{ __html: tl(c.texto_copyright) || `© ${new Date().getFullYear()} ${proyecto?.nombre || 'MentorIA'}. Todos los derechos reservados.` }} />
          </BottomSide>
          {(copyrightImg || c.texto_derecha) && (
            <BottomSide>
              {c.texto_derecha && <span>{tl(c.texto_derecha)}</span>}
              {copyrightImg && <CopyImg src={copyrightImg} $h={c.copyright_imagen_altura} $w={c.copyright_imagen_ancho} />}
            </BottomSide>
          )}
        </BottomRow>
          );
        })()}
      </Inner>
        );
      })()}
    </Wrapper>
    </>
  );
};

// Pie escrito a mano (`modo: 'html'`): el HTML y el CSS de la sección se pintan
// tal cual, con las mismas animaciones de entrada que un bloque HTML. Sigue
// siendo de tipo footer, así que también sale en las páginas internas.
const FooterConModos = (props) => {
  if (props.config?.modo === 'streaming') return <FooterStreaming {...props} />;
  if (props.config?.modo === 'curva') return <PieCurva {...props} />;
  return props.config?.modo === 'html' && props.config?.html
    ? <CustomHtmlSection config={props.config} />
    : <FooterSection {...props} />;
};

export default FooterConModos;
